// services/billingService.js
// Billing & Time Tracking Service — Updated with firmId support

import { db } from '../firebase-config';
import { 
  collection, addDoc, getDocs, updateDoc, deleteDoc, doc, 
  query, where, serverTimestamp 
} from 'firebase/firestore';

// Helper: query with firmId fallback to userId
const queryWithFirmFallback = async (collectionName, userId, firmId, extraFilters = []) => {
  let results = [];
  
  // Try firmId first
  if (firmId) {
    let q = query(collection(db, collectionName), where('firmId', '==', firmId), ...extraFilters);
    const snapshot = await getDocs(q);
    results = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
  }
  
  // No creator-field (userId-only) fallback: firm data is read ONLY through the
  // current firm's tenant-scoped query above. A userId-only query cannot be
  // proven against the active-membership rules and is denied outright; a
  // firm-constrained variant would be a strict subset of the query above.
  return results;
};

// ============================================================================
// TIME ENTRIES
// ============================================================================

export const createTimeEntry = async (userId, entryData, firmId) => {
  try {
    const docRef = await addDoc(collection(db, 'timeEntries'), {
      ...entryData,
      userId,
      firmId: firmId,
      billable: entryData.billable !== false,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    return { success: true, id: docRef.id };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const getTimeEntries = async (userId, filters = {}, firmId) => {
  try {
    let entries = await queryWithFirmFallback('timeEntries', userId, firmId);

    if (filters.caseId) entries = entries.filter(e => e.caseId === filters.caseId);
    if (filters.startDate) entries = entries.filter(e => e.date >= filters.startDate);
    if (filters.endDate) entries = entries.filter(e => e.date <= filters.endDate);
    if (filters.billable !== undefined) entries = entries.filter(e => e.billable === filters.billable);

    entries.sort((a, b) => {
      const da = a.createdAt?.toDate?.() || new Date(0);
      const db2 = b.createdAt?.toDate?.() || new Date(0);
      return db2 - da;
    });

    return { success: true, entries };
  } catch (error) {
    return { success: false, error: error.message, entries: [] };
  }
};

export const updateTimeEntry = async (entryId, updates) => {
  try {
    await updateDoc(doc(db, 'timeEntries', entryId), {
      ...updates,
      updatedAt: serverTimestamp()
    });
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const deleteTimeEntry = async (entryId) => {
  try {
    await deleteDoc(doc(db, 'timeEntries', entryId));
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// ============================================================================
// INVOICES
// ============================================================================

export const createInvoice = async (userId, invoiceData, firmId) => {
  try {
    const invoiceNumber = `INV-${Date.now().toString(36).toUpperCase()}`;
    const docRef = await addDoc(collection(db, 'invoices'), {
      ...invoiceData,
      invoiceNumber,
      userId,
      firmId: firmId,
      status: invoiceData.status || 'draft',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    return { success: true, id: docRef.id, invoiceNumber };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const getInvoices = async (userId, filters = {}, firmId) => {
  try {
    let invoices = await queryWithFirmFallback('invoices', userId, firmId);

    if (filters.status && filters.status !== 'all') {
      invoices = invoices.filter(i => i.status === filters.status);
    }
    if (filters.clientName) {
      invoices = invoices.filter(i =>
        i.clientName?.toLowerCase().includes(filters.clientName.toLowerCase())
      );
    }

    invoices.sort((a, b) => {
      const da = a.createdAt?.toDate?.() || new Date(0);
      const db2 = b.createdAt?.toDate?.() || new Date(0);
      return db2 - da;
    });

    return { success: true, invoices };
  } catch (error) {
    return { success: false, error: error.message, invoices: [] };
  }
};

export const updateInvoice = async (invoiceId, updates) => {
  try {
    await updateDoc(doc(db, 'invoices', invoiceId), {
      ...updates,
      updatedAt: serverTimestamp()
    });
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const deleteInvoice = async (invoiceId) => {
  try {
    await deleteDoc(doc(db, 'invoices', invoiceId));
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// ============================================================================
// CREDIT NOTES — formal reductions against an invoice (negotiated discounts,
// disputes, partial waivers). Issued invoices are immutable; credit notes
// reduce the outstanding balance while preserving the audit trail.
// ============================================================================

export const createCreditNote = async (userId, creditData, firmId) => {
  try {
    const seq = Date.now().toString(36).toUpperCase().slice(-5);
    const creditNumber = (creditData.invoiceNumber ? `${creditData.invoiceNumber}-CN-${seq}` : `CN-${seq}`);
    const docRef = await addDoc(collection(db, 'creditNotes'), {
      ...creditData,
      creditNumber,
      amount: parseFloat(creditData.amount) || 0,
      userId,
      firmId: firmId || null,
      issuedAt: serverTimestamp(),
      createdAt: serverTimestamp()
    });
    return { success: true, id: docRef.id, creditNumber };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const getCreditNotes = async (userId, filters = {}, firmId) => {
  try {
    let credits = await queryWithFirmFallback('creditNotes', userId, firmId);
    if (filters.invoiceId) credits = credits.filter(c => c.invoiceId === filters.invoiceId);
    if (filters.clientId) credits = credits.filter(c => c.clientId === filters.clientId);
    credits.sort((a, b) => {
      const da = a.issuedAt?.toDate?.() || a.createdAt?.toDate?.() || new Date(0);
      const db2 = b.issuedAt?.toDate?.() || b.createdAt?.toDate?.() || new Date(0);
      return db2 - da;
    });
    return { success: true, credits };
  } catch (error) {
    return { success: false, error: error.message, credits: [] };
  }
};

export const deleteCreditNote = async (creditId) => {
  try {
    await deleteDoc(doc(db, 'creditNotes', creditId));
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};